import { z } from 'zod';
import { uuidSchema } from './common.schema';

// ── Create request ────────────────────────────────────────────────────────────

export const CreateSecTraceabilitySchema = z.object({
  /** FK to the feed_prep_splits row that triggered SEC generation. */
  feed_prep_split_id: uuidSchema,

  /** UUID of the generating hospital (denormalised for fast audit lookup). */
  hospital_id: uuidSchema,

  /**
   * The 40-character encoded SEC — barcode payload, no delimiter, no 'SEC' prefix.
   * Format: CC(2) + TE(6) + UDN(13) + CS(1) + ProductNr(7) + SplitNr(3) + Expiry(8) = 40 chars.
   */
  canonical_id: z.string().length(40, 'canonical_id must be exactly 40 characters'),

  /**
   * Human-readable form printed on the label and shown on screen.
   * Format: 'SEC <DIS21>/<PIS19>', max 46 chars.
   */
  sec_human_readable: z.string().min(1).max(50),

  /** EUTC product code captured at generation time (e.g. 'E0000110'). Omit for ISBT 128. */
  eutc_id: z.string().max(20).optional().nullable(),

  /** ISBT 128 product code captured at generation time. Omit for EUTC. */
  isbt_id: z.string().max(20).optional().nullable(),

  /** Snapshot of the TE code used when this SEC was generated. */
  te_code: z.string().max(6).optional().nullable(),

  /** ICCBBA FIN — populated only for ISBT 128. */
  fin_code: z.string().max(5).optional().nullable(),
});

export type CreateSecTraceabilityRequest = z.infer<typeof CreateSecTraceabilitySchema>;

// ── Response item ─────────────────────────────────────────────────────────────

export const SecTraceabilityItemSchema = z.object({
  sec_traceability_id: uuidSchema,
  feed_prep_split_id:  uuidSchema,
  hospital_id:         uuidSchema,
  canonical_id:        z.string(),
  sec_human_readable:  z.string(),
  eutc_id:             z.string().nullable(),
  isbt_id:             z.string().nullable(),
  te_code:             z.string().nullable(),
  fin_code:            z.string().nullable(),
  created_at:          z.string(),
});

export type SecTraceabilityItem = z.infer<typeof SecTraceabilityItemSchema>;

// ── List response ─────────────────────────────────────────────────────────────

export const SecTraceabilityListResponseSchema = z.object({
  items: z.array(SecTraceabilityItemSchema),
});

export type SecTraceabilityListResponse = z.infer<typeof SecTraceabilityListResponseSchema>;
